import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

export interface DriverAccess {
  id: string;
  driver_id: string;
  pin_code: string;
  is_active: boolean;
  last_login_at: string | null;
  device_id: string | null;
  created_at: string;
  driver_name?: string;
  driver_cpf?: string;
  driver_status?: string;
}

export interface DriverWithAccess {
  id: string;
  name: string;
  cpf: string;
  status: string;
  has_access: boolean;
  access_id?: string;
  is_active?: boolean;
  last_login_at?: string | null;
  pin_code?: string;
}

export function useDriverAccess(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Buscar todos os motoristas com status de acesso
  const { data: driversWithAccess = [], isLoading } = useQuery({
    queryKey: ['driver-access', companyId],
    queryFn: async () => {
      if (!companyId) return [];

      // Buscar motoristas
      const { data: drivers, error: driversError } = await supabase
        .from('fleet_drivers')
        .select('id, name, cpf, status')
        .eq('company_id', companyId)
        .eq('status', 'active')
        .order('name');

      if (driversError) throw driversError;

      // Buscar acessos mobile
      const { data: accesses, error: accessError } = await supabase
        .from('fleet_driver_auth')
        .select('*');

      if (accessError) throw accessError;

      // Combinar dados
    const result: DriverWithAccess[] = (drivers || []).map(driver => {
      const access = accesses?.find(a => a.driver_id === driver.id);
      return {
        ...driver,
        has_access: !!access,
        access_id: access?.id,
        is_active: access?.is_active || false,
        last_login_at: access?.last_login_at || null
      };
    });

      return result;
    },
    enabled: !!companyId
  });

  // Criar acesso mobile
  const createAccessMutation = useMutation({
    mutationFn: async ({ driverId, pinCode }: { driverId: string; pinCode: string }) => {
      // Validar PIN
      if (!pinCode || pinCode.length < 4 || pinCode.length > 6) {
        throw new Error('PIN deve ter entre 4 e 6 dígitos');
      }

      if (!/^\d+$/.test(pinCode)) {
        throw new Error('PIN deve conter apenas números');
      }

      // Verificar PINs fracos
      const weakPins = ['1234', '0000', '1111', '2222', '3333', '4444', '5555', '6666', '7777', '8888', '9999'];
      if (weakPins.includes(pinCode)) {
        throw new Error('PIN muito simples. Escolha uma combinação mais segura');
      }

      const { data, error } = await supabase
        .from('fleet_driver_auth')
        .insert({
          driver_id: driverId,
          pin_code: pinCode,
          is_active: true
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['driver-access'] });
      toast({
        title: 'Acesso mobile configurado!',
        description: 'PIN definido com sucesso. Forneça o PIN ao motorista.'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao configurar acesso',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Atualizar acesso
  const updateAccessMutation = useMutation({
    mutationFn: async ({ driverId, updates }: { driverId: string; updates: Partial<DriverAccess> }) => {
      const { data, error } = await supabase
        .from('fleet_driver_auth')
        .update(updates)
        .eq('driver_id', driverId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['driver-access'] });
      toast({
        title: 'Acesso atualizado com sucesso!'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atualizar acesso',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Alternar status do acesso
  const toggleAccessMutation = useMutation({
    mutationFn: async ({ driverId, isActive }: { driverId: string; isActive: boolean }) => {
      const { data, error } = await supabase
        .from('fleet_driver_auth')
        .update({ is_active: !isActive })
        .eq('driver_id', driverId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['driver-access'] });
      toast({
        title: data.is_active ? 'Acesso ativado' : 'Acesso desativado',
        description: data.is_active 
          ? 'O motorista pode fazer login no app' 
          : 'O motorista não pode mais fazer login'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao alterar status',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Redefinir PIN
  const resetPinMutation = useMutation({
    mutationFn: async ({ driverId, newPin }: { driverId: string; newPin: string }) => {
      // Validações
      if (!newPin || newPin.length < 4 || newPin.length > 6) {
        throw new Error('PIN deve ter entre 4 e 6 dígitos');
      }

      if (!/^\d+$/.test(newPin)) {
        throw new Error('PIN deve conter apenas números');
      }

      const weakPins = ['1234', '0000', '1111', '2222', '3333', '4444', '5555', '6666', '7777', '8888', '9999'];
      if (weakPins.includes(newPin)) {
        throw new Error('PIN muito simples. Escolha uma combinação mais segura');
      }

      const { data, error } = await supabase
        .from('fleet_driver_auth')
        .update({ pin_code: newPin })
        .eq('driver_id', driverId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['driver-access'] });
      toast({
        title: 'PIN redefinido com sucesso!',
        description: 'Novo PIN definido. Forneça o PIN ao motorista.'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao redefinir PIN',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  return {
    driversWithAccess,
    isLoading,
    createAccess: createAccessMutation.mutate,
    updateAccess: updateAccessMutation.mutate,
    toggleAccess: toggleAccessMutation.mutate,
    resetPin: resetPinMutation.mutate,
    isCreating: createAccessMutation.isPending,
    isUpdating: updateAccessMutation.isPending
  };
}
